import React, { useMemo } from "react";
import { Col, Row } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import ContentTabs from "@repoflow/components/content-tabs";
import { useSectionContext } from "@repoflow/components/section";
import { Form as TypesForm } from "@repoflow/components/types-form";
import { ValuesPane, typesHelp } from "components/types-help";
import * as widgets from "widgets/register";
import PagePublicSchema from "@common/schema/page/public";
import TypesSchema from "@common/schema/types";
import { types as paramTypes } from "@common/schema/types/params/utils";

import { useEditorPage } from "../../../context";
import { SharedAcross, ThisLanguageOnly } from "../../../translation";
import * as ConfigRoute from "../../config";

// A `code` element: a section inlined onto one page.
//
// The tabs are the same three the section editor has, for the same reason —
// Code is the markup, Data is the templating layer, Element is what wraps it —
// and they are the same three gen-2 had. Nothing here is a separate form: every
// tab writes into the page's single value object through the element route's
// Section.Provider, so Save in the tab strip above covers all of them.
const CODE_FIELDS = {
  "/Page/Element/Type/Code": {
    fields: [{ property: "html", layout: "wide" }],
  },
};

const STYLE_FIELDS = {
  "/Page/Element/Type/Code": {
    fields: [{ property: "style", layout: "wide" }],
  },
};

const SCRIPT_FIELDS = {
  "/Page/Element/Type/Code": {
    fields: [{ property: "script", layout: "wide" }],
  },
};

// One field per tab, all three writing `config`.
//
// `config` and not the element: the three fields belong to the branch of the
// oneOf that `type` selected, and writing them onto the element itself would
// put them somewhere the schema does not declare and the API would reject.
const CodeField = ({ config }) => {
  const { schema, state } = useSectionContext();
  const { values, setValues } = state;
  // Markup, not data. A `code` element is an anonymous section and splits the
  // same way one does: translating text typed in here means parameterising it
  // into a value first, which is the Data tab beside this one.
  const { languages } = useEditorPage();

  return (
    <Row>
      <Col>
        <SharedAcross languages={languages} />
        <Form
          schema={schema}
          target={{ $ref: "/Page/Element/Type/Code" }}
          widgets={widgets}
          values={values.config || {}}
          onChange={(mod) => setValues({ ...values, config: mod })}
          config={config}
        />
      </Col>
    </Row>
  );
};

const CodeTab = () => (
  <ContentTabs
    tabs={[
      { label: "HTML", content: <CodeField config={CODE_FIELDS} /> },
      { label: "Style", content: <CodeField config={STYLE_FIELDS} /> },
      { label: "Script", content: <CodeField config={SCRIPT_FIELDS} /> },
    ]}
  />
);

// The two halves of the templating layer: Types declares the slots, Values
// fills them. Neither is an ordinary schema field — `values` has no fixed
// shape, and the schema for it is compiled out of `types` at render time.
const DataTab = () => {
  const { state } = useSectionContext();
  const { values, setValues } = state;
  const { languages } = useEditorPage();
  const config = values.config || {};

  const setConfig = (mod) => setValues({ ...values, config: mod });

  return (
    <ContentTabs
      tabs={[
        {
          label: "Values",
          content: (
            <Row>
              <Col>
                {/* The one half of this element that every language keeps its
                    own copy of. */}
                <ThisLanguageOnly languages={languages} />
                <ValuesPane
                  context="element"
                  types={config.types}
                  values={config.values}
                  widgets={widgets}
                  // The generated schema references /Image and friends by $ref,
                  // and an element's own types may too. Passing the shared
                  // fragments in means a param can use them without
                  // types-values knowing they exist.
                  utils={PagePublicSchema}
                  onChange={(mod) => setConfig({ ...config, values: mod })}
                />
              </Col>
            </Row>
          ),
        },
        {
          label: "Types",
          content: (
            <Row>
              <Col>
                {/* The declaration, which is the group's — the slots are the
                    same in every language, and only what is filled into them
                    differs. */}
                <SharedAcross languages={languages} />
                <TypesForm
                  help={typesHelp("element")}
                  schema={TypesSchema}
                  paramTypes={paramTypes}
                  widgets={widgets}
                  values={{ types: config.types }}
                  onChange={({ types }) => setConfig({ ...config, types })}
                />
              </Col>
            </Row>
          ),
        },
      ]}
    />
  );
};

export const Content = () => {
  const tabs = useMemo(
    () => [
      { label: "Code", content: <CodeTab /> },
      { label: "Data", content: <DataTab /> },
      { label: "Element", content: <ConfigRoute.Content /> },
    ],
    []
  );

  return <ContentTabs tabs={tabs} />;
};
